<script lang="ts">
	import { resolve } from '$app/paths';
	import { toaster } from '$lib/shared/lib/toaster';
	import { Dialog, Portal } from '@skeletonlabs/skeleton-svelte';
	import { navigating } from '$app/state';
	import { browser } from '$app/environment';
	import ShowSelectDropdown from '$lib/features/admin/main/ui/ShowSelectDropdown.svelte';
	import SideNavBar from '$lib/features/admin/main/ui/SideNavBar.svelte';
	import AvatarComponent from '$lib/shared/ui/AvatarComponent.svelte';
	import PopupAccountBox from '$lib/features/user/ui/PopupAccountBox.svelte';
	import NotificationBanner from '$lib/shared/ui/NotificationBanner.svelte';
	import ThemeToggle from '$components/ThemeToggle.svelte';
	import { notificationBanner } from '$lib/shared/lib/notificationStore';
	import { openDrawer as openMobileDrawer } from '$lib/shared/lib/drawerStore';
	import { SIDEBAR_BREAKPOINT } from '$lib/shared/lib/constants';
	import Fa from 'svelte-fa';
	import {
		faBars,
		faBullhorn,
		faChevronLeft,
		faChevronRight
	} from '@fortawesome/free-solid-svg-icons';
	import { onMount } from 'svelte';
	import MotdModal from '$lib/features/motd/ui/MotdModal.svelte';
	import { fetchMotdChecksum, fetchMotdContent } from '$lib/features/motd/api/motdApi';
	import { motdLastSeenStore } from '$lib/features/motd/lib/motdStore';
	import type { User } from '$lib/shared/lib/types';

	interface Props {
		user: User | null;
		headerAbsolute?: boolean;
		mainFullHeight?: boolean;
		children?: import('svelte').Snippet<[{ user: User | null }]>;
	}

	let {
		user = $bindable(),
		headerAbsolute = false,
		mainFullHeight = false,
		children
	}: Props = $props();

	let bannerHeight = $state(0);
	let isUnread = $state(false);

	// Sidebar collapse state with localStorage persistence
	let sidebarCollapsed = $state(
		browser ? localStorage.getItem('sidebarCollapsed') === 'true' : false
	);

	function toggleSidebar() {
		sidebarCollapsed = !sidebarCollapsed;
		if (browser) localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed));
	}

	function openDrawer() {
		openMobileDrawer();
	}

	// MOTD modal — Skeleton 4 inline <Dialog> with explicit open state lift.
	let motdOpen = $state(false);
	let motdContent = $state('');
	let motdChecksum = $state('');
	let motdTitle = $state('');

	// Check if MOTD exists and if it's unread
	onMount(async () => {
		const checksumData = await fetchMotdChecksum();
		if (checksumData) {
			const lastSeenChecksum = $motdLastSeenStore;
			isUnread = !lastSeenChecksum || lastSeenChecksum !== checksumData.checksum;
		}
	});

	async function showMotdModal() {
		const checksumData = await fetchMotdChecksum();
		if (!checksumData) {
			toaster.info({ title: 'Info', description: 'No admin message available at this time.' });
			return;
		}

		const data = await fetchMotdContent(checksumData.s3_url);
		if (!data) {
			toaster.error({ title: 'Error', description: 'Failed to load admin message.' });
			return;
		}

		motdContent = data.content;
		motdChecksum = checksumData.checksum;
		motdTitle = data.title;
		motdOpen = true;

		// Update unread status after showing
		isUnread = false;
	}

	const logoBlackSrc = '/static/images/logo/supertip-revised-black.avif';
	const logoWhiteSrc = '/static/images/logo/supertip-revised-white.avif';
	const logoSmBlackSrc = '/static/images/logo/supertip-s-black.avif';
	const logoSmWhiteSrc = '/static/images/logo/supertip-s-white.avif';
	const lightLeft = '/static/images/light-left.avif';
	const lightRight = '/static/images/light-right.avif';

	let iconClasses = 'h-10 my-3 sm:mb-0 sm:h-14  sm:mt-7';

	// Build conditional classes
	let headerClasses = $derived(
		headerAbsolute
			? 'right-0 z-20 flex items-center justify-between p-3 sm:absolute'
			: 'z-20 flex items-center justify-between p-1 sm:p-3'
	);

	let mainClasses = $derived(
		mainFullHeight ? 'col-span-1 h-full overflow-y-scroll' : 'col-span-1 overflow-y-scroll'
	);

	// Dynamic spacing when banner is shown
	let contentStyle = $derived($notificationBanner.show ? `padding-top: ${bannerHeight}px` : '');

	function handleBannerClose() {
		notificationBanner.hide();
	}
</script>

<!-- SIDEBAR_BREAKPOINT just does not work with the dynamic grid-cols-[] even when safelisted in tailwind -->
<!-- If changing SIDEBAR_BREAKPOINT you need to update the value tied to lg:grid-cols-[auto_1fr] here too -->
<div class="fixed grid h-dvh w-screen grid-cols-1 lg:grid-cols-[auto_1fr]">
	<!-- Notification Banner -->
	{#if $notificationBanner.show}
		<div class="absolute top-0 right-0 left-0 z-50">
			<NotificationBanner
				message={$notificationBanner.message}
				show={$notificationBanner.show}
				type={$notificationBanner.type}
				dismissible={$notificationBanner.dismissible}
				bind:bannerHeight
				onclose={handleBannerClose}
			/>
		</div>
	{/if}

	<div class="pointer-events-none absolute top-0 left-0 z-0 h-1/5 w-1/5">
		<img src={lightLeft} alt="" class="" draggable="false" />
	</div>
	<div class="pointer-events-none absolute top-0 right-0 z-0 h-1/5 w-1/5">
		<img src={lightRight} alt="" class="" draggable="false" />
	</div>
	<aside class="relative hidden {SIDEBAR_BREAKPOINT}:block" style={contentStyle}>
		<div class="flex flex-row">
			<div
				class="relative flex h-dvh flex-col transition-all duration-300 {sidebarCollapsed
					? 'px-0'
					: 'px-11'}"
			>
				<div class="block dark:hidden">
					<a href={resolve('/')}
						><img
							alt="logo"
							src={sidebarCollapsed ? logoSmBlackSrc : logoBlackSrc}
							class={iconClasses}
						/></a
					>
				</div>
				<div class="hidden dark:block">
					<a href={resolve('/')}
						><img
							alt="logo"
							src={sidebarCollapsed ? logoSmWhiteSrc : logoWhiteSrc}
							class={iconClasses}
						/></a
					>
				</div>
				<div class="h-full sm:pt-5 sm:pb-14">
					<SideNavBar {user} collapsed={sidebarCollapsed} />
				</div>
				<button
					onclick={toggleSidebar}
					class="mt-auto mb-8 flex items-center justify-center rounded p-2
					   transition-colors hover:bg-surface-100-900"
				>
					<Fa icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
				</button>
			</div>
			<!-- Desktop side nav blue border gradient -->
			<div class="h-dvh w-px bg-gradient-to-b from-primary-500/50 to-transparent"></div>
		</div>
	</aside>
	<div class="relative flex flex-col overflow-hidden pr-0 pb-8 pl-0 sm:pb-5" style={contentStyle}>
		<!-- custom hamburger position for panel page to accomodate play controls -->
		{#if headerAbsolute}
			<div class="absolute left-0 z-40 pt-5 pl-5 {SIDEBAR_BREAKPOINT}:hidden">
				<button onclick={() => openDrawer()}>
					<Fa icon={faBars} size="1.5x" />
				</button>
			</div>
		{/if}
		<!-- absolute position on desktop to allow play controls to be positioned at the top of the panel page -->
		<header id="new-header" class={headerClasses}>
			<div class="pl-2 {SIDEBAR_BREAKPOINT}:hidden">
				{#if !headerAbsolute}
					<button onclick={() => openDrawer()}>
						<Fa icon={faBars} size="1.5x" />
					</button>
				{/if}
			</div>
			<div class="hidden {SIDEBAR_BREAKPOINT}:block"></div>
			<div class="flex items-center gap-2 md:justify-end lg:gap-4">
				<div class="flex">
					<ShowSelectDropdown {user} />
				</div>
				<div class="flex items-center space-x-1 lg:space-x-4">
					<button
						class="relative inline-flex h-6 items-center justify-center rounded-full border border-surface-500 preset-tonal-surface px-3 text-xs"
						onclick={showMotdModal}
						title="View Admin Message"
					>
						<Fa icon={faBullhorn} />
						{#if isUnread}
							<span class="absolute -top-1 -right-1 flex h-3 w-3">
								<span
									class="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-500 opacity-75"
								></span>
								<span class="relative inline-flex h-3 w-3 rounded-full bg-primary-500"></span>
							</span>
						{/if}
					</button>
					<span class="desktop-only h-6 border-l border-surface-400-600"></span>
					<ThemeToggle />
					<span class="desktop-only h-6 border-l border-surface-400-600"></span>
					<AvatarComponent logged={!!user} src={user?.avatar}>
						<PopupAccountBox {user} />
					</AvatarComponent>
				</div>
			</div>
		</header>

		<main class={mainClasses}>
			{#if navigating.to}
				<div class="flex h-dvh w-full animate-pulse items-center justify-center">
					<img alt="Loading" src={logoBlackSrc} class="block h-20 dark:hidden" />
					<img alt="Loading" src={logoWhiteSrc} class="hidden h-20 dark:block" />
				</div>
			{:else}
				{@render children?.({ user })}
			{/if}
		</main>
	</div>
</div>

<Dialog open={motdOpen} onOpenChange={(d) => (motdOpen = d.open)}>
	<Portal>
		<Dialog.Backdrop class="fixed inset-0 z-50 bg-surface-50-950/50" />
		<Dialog.Positioner class="fixed inset-0 z-50 flex items-center justify-center">
			<Dialog.Content class="w-full max-w-2xl space-y-4 card bg-surface-100-900 p-4">
				<MotdModal
					content={motdContent}
					checksum={motdChecksum}
					title={motdTitle}
					ondismiss={() => (motdOpen = false)}
				/>
			</Dialog.Content>
		</Dialog.Positioner>
	</Portal>
</Dialog>

<style>
</style>
